import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Button from "../../../../../app/components/Button";
import Loader from "../../../../../app/components/Loader";
import Input from "../../../../../app/components/Input";
import {
  FiPlus,
  FiList,
  FiFileText,
  FiMail,
  FiBarChart2,
  FiSettings
} from "react-icons/fi";
import KanbanBoard from "../../../../../app/components/Kanban";
import Documentos from "./tabs/ReportSequence/ReportSequence";
import Emails from "./tabs/EmailsSequences";
import Panel from "./tabs/Panel";
import OffCanvasSettings from "./tabs/Settings";
import FormNovaSequencia from "../../../../../forms/NewSequence/NewSequenceAdd";
import CardDetailForm from "../../../../../forms/FormCardDetail/FormCardDetail";
import { usePageTitle } from '../../../../../contexts/PageTitleContext';


const initialColumns = {
  negotiation: {
    id: "negotiation",
    title: "Em negociação",
    items: [
      {
        id: "example-1",
        titulo: "FOLHA DE PAGAMENTO",
        tags: ["Maio", "Departamento Pessoal"],
        client: "132 - BUMBA - ESCOLA",
        acumulador: "Maio",
        competencia: "01/04/2025",
        prazoMeta: "06/05/2025 00:00",
        responsavel: "Natallia Marissa",
        departamento: "DEPARTAMENTO PESSOAL",
        regime: "SIMPLES NACIONAL - SERVIÇO - ...",
        vencimento: "Venc mai, 6",
        reminders: [
          { id: "r1", days: 112, type: "warning" },
          { id: "r2", days: 57, type: "info" }
        ]
      },
      {
        id: "example-2",
        titulo: "FOLHA DE PAGAMENTO",
        tags: ["Maio", "Departamento Pessoal"],
        client: "ACME LTDA",
        acumulador: "Maio",
        competencia: "01/04/2025",
        prazoMeta: "06/05/2025 00:00",
        responsavel: "Fulano da Silva",
        departamento: "DEPARTAMENTO PESSOAL",
        regime: "LUCRO PRESUMIDO - SERVIÇO",
        vencimento: "Venc mai, 10",
        reminders: [
          { id: "r3", days: 90, type: "warning" },
          { id: "r4", days: 45, type: "info" }
        ]
      }
    ]
  },
  toPay: { id: "toPay", title: "A pagar", count: 0, items: [] },
  paid: { id: "paid", title: "Pagos", count: 0, items: [] },
  waitingForDocuments: { id: "waitingForDocuments", title: "Aguardando documentos", count: 0, items: [] },
  completed: { id: "completed", title: "Concluídos", count: 0, items: [] }
};

export default function SequenceDetails() {    
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  const queryParams = new URLSearchParams(location.search);
  const sequenceId = queryParams.get("id");
  const tabFromQuery = queryParams.get("tab") || "Lista";
  const [activeTab, setActiveTab] = useState(tabFromQuery);

  const [columns, setColumns] = useState(initialColumns);
  const [selectedCard, setSelectedCard] = useState(null);

  const [sequenceTitle, setSequenceTitle] = useState("Sequência");
  const { setTitle } = usePageTitle();
  
  useEffect(() => {
    setTitle(sequenceTitle);
  }, [setTitle, sequenceTitle]);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isCardDetailOpen, setIsCardDetailOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  const withCounts = (columns) => {
    return Object.fromEntries(
      Object.entries(columns).map(([colId, col]) => [
        colId,
        { ...col, count: col.items.length }
      ])
    );
  };


  useEffect(() => {
    const q = new URLSearchParams(location.search);
    const tab = q.get("tab") || "Lista";
    if (tab !== activeTab) {
      setActiveTab(tab);
    }
  }, [location.search, activeTab]);

  useEffect(() => {
    if (!sequenceId) return;

    const saved = localStorage.getItem(`kanban-${sequenceId}`);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        const fullStructure = { ...initialColumns };

        Object.keys(fullStructure).forEach(colId => {
          fullStructure[colId].items = [];
        });

        Object.values(parsed).forEach(col => {
          col.items.forEach((item) => {
            const colId = item.columnId || col.id;
            if (!fullStructure[colId]) {
              fullStructure[colId] = {
                id: colId,
                title: parsed[colId]?.title || col.title || colId,
                items: []
              };
            }
            fullStructure[colId].items.push(item);
          });
        });

        setColumns(withCounts(fullStructure));
      } catch (error) {
        setColumns(withCounts(initialColumns));
      }
    } else {
      setColumns(withCounts(initialColumns));
    }

    setIsLoading(false);
  }, [sequenceId]);


  useEffect(() => {
    if (location.state?.sequenceTitle) {
      setSequenceTitle(location.state.sequenceTitle);
      return;
    }

    if (!sequenceId) return;

    const sequences = JSON.parse(localStorage.getItem("sequences")) || [];
    const found = sequences.find(seq => seq.id === sequenceId);
    if (found?.title) {
      setSequenceTitle(found.title);
    }
  }, [sequenceId, location.state]);




  useEffect(() => {
    if (sequenceId) {
      localStorage.setItem(`kanban-${sequenceId}`, JSON.stringify(columns));
    }
  }, [columns, sequenceId]);

  useEffect(() => {
    const q = new URLSearchParams(location.search);
    if (q.get("tab") !== activeTab) {
      q.set("tab", activeTab);
      navigate(`${location.pathname}?${q.toString()}`, { replace: true });
    }
  }, [activeTab, navigate, location.pathname, location.search]);

  const handleCardClick = (card, columnId) => {
    setSelectedCard({ ...card, columnId });
    setIsCardDetailOpen(true);
  };

  const handleCloseCardDetail = () => {
    setIsCardDetailOpen(false);
  };
  

  const updateCard = (updatedCard) => {
    const columnId = Object.keys(columns).find((colId) =>
      columns[colId].items.some((item) => item.id === updatedCard.id)
    );

    if (columnId) {
      const updatedColumns = { ...columns };
      const cardIndex = updatedColumns[columnId].items.findIndex(
        (item) => item.id === updatedCard.id
      );
      if (cardIndex !== -1) {
        updatedColumns[columnId].items[cardIndex] = { ...updatedCard, columnId };
        setColumns(withCounts(updatedColumns));
      }
    }
  };

  const handleSubmitForm = (formData) => {
    if (!formData) return;
  
    const newCard = {
      id: `card-${Date.now()}`,
      titulo: formData.service || "",
      client: formData.nomeCliente || "",
      razaoSocial: formData.razaoSocial || formData.nomeCliente || "",
      nomeFantasia: formData.nomeFantasia || "",
      email: formData.email || "",
      telefone: formData.telefone || "",
      cnpj: formData.cnpj || "",
      value: formData.value || "",
      service: formData.service || "",
      dataFechamento: formData.dataFechamento || "",
      temperatura: formData.temperatura || "",
      observacoes: formData.observacoes || "",
      anexos: formData.anexos || [],
      tags: formData.tags || [],
      acumulador: formData.acumulador || "",
      competencia: formData.competencia || "",
      prazoMeta: formData.prazoMeta || "",
      responsavel: formData.responsavel || "",
      departamento: formData.departamento || "",
      regime: formData.regime || "",
      vencimento: formData.vencimento || "",
      reminders: formData.reminders || [
        { id: `r-${Date.now()}`, days: 30, type: "info" }
      ],
      columnId: "negotiation",
      hasEmail: !!formData.email,
      hasFile: (formData.anexos || []).length > 0,
      fileCount: (formData.anexos || []).length || undefined,
      hasObservacoes: !!formData.observacoes,
      lastUpdate: "agora",
      messageCount: 0
    };
  
    const updatedColumns = { ...columns };
    updatedColumns.negotiation.items.push(newCard);
    setColumns(withCounts(updatedColumns));
    localStorage.setItem(`kanban-${sequenceId}`, JSON.stringify(withCounts(updatedColumns)));
    setIsFormOpen(false);

  };

  return (
    <div title={sequenceTitle}>
      {isLoading ? (
        <div className="flex justify-center items-center h-[50vh]">
          <Loader width="w-8" height="h-8" />
        </div>
      ) : (
      <div className="flex flex-col w-full h-full">

        <div className="w-full px-4 py-3 flex justify-between items-start">
          {activeTab === "Lista" ? (
            <div className="flex gap-1">
              <Button variant="primary" onClick={() => setIsFormOpen(true)} 
              className="flex items-center justify-center !h-9 !w-9 !px-5 sm:h-auto sm:w-auto sm:px-6"
              >
                <FiPlus size={16} />
              </Button>
              <Input type="text" size="xs" variant="search" label="Pesquisar" />
            </div>
          ) : (
            <div />
          )}

          <div className="flex gap-1 items-center">
            {["Lista", "Documentos", "Emails", "Estatísticas"].map((tab) => (
              <button
                key={tab}
                className={`p-2 rounded ${
                  activeTab === tab ? "bg-gray-100 text-gray-600" : "hover:bg-gray-100 text-gray-600"
                }`}
                onClick={() => setActiveTab(tab)}
              >
                {{
                  Lista: <FiList size={18} />,
                  Documentos: <FiFileText size={18} />,
                  Emails: <FiMail size={18} />,
                  Estatísticas: <FiBarChart2 size={18} />,
                }[tab]}
              </button>
            ))}

            <button
              className="p-2 rounded hover:bg-gray-100 text-gray-600"
              onClick={() => setIsSettingsOpen(true)}
              aria-label="Abrir configurações"
            >
              <FiSettings size={18} />
            </button>
          </div>
        </div>

        <div className="flex-1 h-full">
          {activeTab === "Lista" && (
            <KanbanBoard
              columns={columns}
              setColumns={setColumns}
              onCardClick={handleCardClick}
            />
          )}
          {activeTab === "Documentos" && <Documentos />}
          {activeTab === "Emails" && <Emails />}
          {activeTab === "Estatísticas" && <Panel />}
        </div>
      </div>
    )}


      <FormNovaSequencia
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSubmit={handleSubmitForm}
      />

      <CardDetailForm
        isOpen={isCardDetailOpen}
        onClose={handleCloseCardDetail}
        cardData={selectedCard}
        updateCard={updateCard}
      />

      <OffCanvasSettings
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
      />
      
    </div>
  );
}
